import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useDataProvider, useLogin, useNotify, useTranslate } from "ra-core";
import { useForm, type SubmitHandler } from "react-hook-form";
import { Navigate, useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import type { CrmDataProvider } from "../providers/types";
import type { SignUpData } from "../types";
import { AuthLayout } from "./AuthLayout";
import { LoginSkeleton } from "./LoginSkeleton";
import { ConfirmationRequired } from "./ConfirmationRequired";
import { OIDCAuthButton } from "./OIDCAuthButton";
import { SSOAuthButton } from "./SSOAuthButton";
import { googleWorkplaceDomain, oidcProvider } from "./authConfig";

// The shadcn Input and Label are themed for a light surface; on the glass card
// of AuthLayout they need an explicit dark treatment. `dark:` overrides included
// because the card stays dark whatever the OS colour scheme says.
const glassInputClassName =
  "bg-white/10 dark:bg-white/10 border-white/20 text-white placeholder:text-gray-300 focus-visible:border-white/40 focus-visible:ring-white/30";

const ssoButtonClassName =
  "w-full rounded-md border border-white/20 bg-white/10 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-white/20";

export const SignupPage = () => {
  const queryClient = useQueryClient();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const navigate = useNavigate();
  const translate = useTranslate();
  const { data: isInitialized, isPending } = useQuery({
    queryKey: ["init"],
    queryFn: async () => {
      return dataProvider.isInitialized();
    },
  });

  const { isPending: isSignUpPending, mutate } = useMutation({
    mutationKey: ["signup"],
    mutationFn: async (data: SignUpData) => {
      return dataProvider.signUp(data);
    },
    onSuccess: (data) => {
      login({
        email: data.email,
        password: data.password,
        redirectTo: "/contacts",
      })
        .then(() => {
          notify("crm.auth.signup.initial_user_created", {
            messageArgs: {
              _: "Initial user successfully created",
            },
          });
          // FIXME: We should probably provide a hook for that in the ra-core package
          queryClient.invalidateQueries({
            queryKey: ["auth", "canAccess"],
          });
        })
        .catch((err) => {
          if (err.code === "email_not_confirmed") {
            // An email confirmation is required to continue.
            navigate(ConfirmationRequired.path);
          } else {
            notify("crm.auth.sign_in_failed", {
              type: "error",
              messageArgs: {
                _: "Failed to log in.",
              },
            });
            navigate("/login");
          }
        });
    },
    onError: (error) => {
      notify(error.message);
    },
  });

  const login = useLogin();
  const notify = useNotify();

  const {
    register,
    handleSubmit,
    formState: { isValid },
  } = useForm<SignUpData>({
    mode: "onChange",
  });

  if (isPending) {
    return <LoginSkeleton />;
  }

  // For the moment, we only allow one user to sign up. Other users must be created by the administrator.
  if (isInitialized) {
    return <Navigate to="/login" />;
  }

  const onSubmit: SubmitHandler<SignUpData> = async (data) => {
    mutate(data);
  };

  return (
    <AuthLayout
      title={translate("crm.auth.welcome_title", {
        _: "Welcome to Atomic CRM",
      })}
      subtitle={translate("crm.auth.signup.create_first_user", {
        _: "Create the first user account to complete the setup.",
      })}
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="first_name" className="text-white">
            {translate("crm.auth.first_name")}
          </Label>
          <Input
            {...register("first_name", { required: true })}
            id="first_name"
            type="text"
            required
            className={glassInputClassName}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="last_name" className="text-white">
            {translate("crm.auth.last_name")}
          </Label>
          <Input
            {...register("last_name", { required: true })}
            id="last_name"
            type="text"
            required
            className={glassInputClassName}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-white">
            {translate("ra.auth.email")}
          </Label>
          <Input
            {...register("email", { required: true })}
            id="email"
            type="email"
            required
            className={glassInputClassName}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-white">
            {translate("ra.auth.password")}
          </Label>
          <Input
            {...register("password", { required: true })}
            id="password"
            type="password"
            required
            className={glassInputClassName}
          />
        </div>
        {/* text-white is explicit: the Button default text-primary-foreground
            turns near-black under the dark theme. */}
        <Button
          type="submit"
          disabled={!isValid || isSignUpPending}
          className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700"
        >
          {isSignUpPending ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {translate("crm.auth.signup.creating", {
                _: "Creating...",
              })}
            </>
          ) : (
            translate("crm.auth.signup.create_account", {
              _: "Create account",
            })
          )}
        </Button>
      </form>

      {/* Outside the <form>: the first account may be created through the IdP
          alone, so these must stay clickable while the fields are empty. */}
      {oidcProvider || googleWorkplaceDomain ? (
        <div className="mt-6">
          <div className="flex items-center gap-3 text-xs text-gray-300">
            <span className="h-px flex-1 bg-white/20" />
            {translate("crm.auth.or_divider", { _: "or" })}
            <span className="h-px flex-1 bg-white/20" />
          </div>
          <div className="mt-4 flex flex-col gap-3">
            {oidcProvider ? (
              <OIDCAuthButton
                provider={oidcProvider}
                className={ssoButtonClassName}
              >
                {translate("crm.auth.sign_in_oidc")}
              </OIDCAuthButton>
            ) : null}
            {googleWorkplaceDomain ? (
              <SSOAuthButton
                domain={googleWorkplaceDomain}
                className={ssoButtonClassName}
              >
                {translate("crm.auth.sign_in_google_workspace", {
                  _: "Sign in with Google Workplace",
                })}
              </SSOAuthButton>
            ) : null}
          </div>
        </div>
      ) : null}
    </AuthLayout>
  );
};

SignupPage.path = "/sign-up";
